<template>
    <table class="date-picker-table">
        <tbody>
            <tr v-for="(row, idx) in tableRows" :key="idx">
                <td
                    v-for="item in row"
                    :key="item.row + '-' + item.column + '-' + item.text"
                    :class="{
                        available: item.available,
                        disabled: item.disabled,
                        'in-range': item.inRange,
                        'selected': item.selected,
                        'start-date': item.start,
                        'end-date': item.end
                    }"
                    @mouseenter.stop="changeRange(item, 'mouseover')"
                    @click.stop="changeRange(item, 'click')"
                >
                    <div class="cell">
                        <div class="inner-cell">
                            <span>{{ item.text }}</span>
                            <small v-show="item.other"> {{ item.other }}</small>
                        </div>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>


<script>

export default {
  name: "DateTable",

  props: {
    rangeState: {
      default: () => ({
        coloumn: null,
        row: null,
        endDate: null,
        selecting: false
      }),
      type: Object
    },

    minDate: {
      default: null,
      type: Object
    },

    maxDate: {
      default: null,
      type: Object
    },

    date: {
      default: null,
      type: Object
    },

    startLimitDate: {
      default: null,
      type: Object
    },

    endLimitDate: {
      default: null,
      type: Object
    },

    page: {
      default: 1,
      type: Number
    },

    pageCount: {
      default: 12,
      type: Number
    },

    singleRowMaxCol: {
      default: 4,
      type: Number
    },

    year: {
      default: undefined,
      type: Number
    },

    rows: {
      default: () => ([]),
      type: Array
    },

    isRange: {
      default: false,
      type: Boolean
    }
  },

  data() {
    return {
      tableRows: []
    };
  },

  computed: {
    _value: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change", val);
      }
    },

    changable() {
      if (this.isRange) {
        
        return {
          rangeState: this.rangeState,
          minDate: this.minDate,
          maxDate: this.maxDate,
          startLimitDate: this.startLimitDate,
          endLimitDate: this.endLimitDate,
          page: this.page
        };
      }

      return {
        rangeState: this.rangeState,
        date: this.date,
        startLimitDate: this.startLimitDate,
        endLimitDate: this.endLimitDate,
        page: this.page
      };
    }
  },

  watch: {
    changable() {
      this.calcRows();
    },

    rows(val) {
      this.calcRows(val);
    }
  },

  methods: {
    calcRows(rows = this.rows, rangeState = this.rangeState) {
      const minDate = this.minDate;
      const maxDate = this.maxDate;
      const date = this.date;

      this.tableRows = rows[this.page - 1].reduce(
        (prev, arr) =>
          prev.concat([
            arr.map(item => this.calcItem(item, minDate, maxDate, rangeState, date))
          ]),
        []
      );
    },

    changeRange(item, action) {
      const { column, row, value, disabled } = item;
      const { selecting } = this.rangeState;

      const year = this.year;
      const page = this.page;

      const targetValue = {
        column,
        row,
        year,
        page,
        value
      };

      if (disabled) {
        return;
      }

      if (this.isRange) {
        if (action === "mouseover") {
          if (selecting) {
            this.$emit("update:rangeState", {
              column,
              row,
              year,
              value,
              page,
              selecting: true
            });
          }
        } else if (action === "click") {
          if (!selecting) {
            // 第一次选择
            this.$emit("update:minDate", targetValue);
            this.$emit("update:maxDate", null);
          } else {

            const minDate = this.minDate;

            const minValue = this.calcCellValue(minDate);
            const maxValue =  this.calcCellValue(targetValue);

            if (minValue <= maxValue) {
              this.$emit("update:maxDate", targetValue);
            } else { // 交换大小值
              this.$emit("update:minDate", targetValue);
              this.$emit("update:maxDate", minDate);
            }

          }

          this.$emit("update:rangeState", {
            column,
            row,
            selecting: !selecting
          });
        }
      } else {
        if (action === "click") {
          this.$emit("update:rangeState", {
            column,
            row
          });
          this.$emit("update:date", targetValue);
        }
      }
    },

    calcItem(item, minDate, maxDate, rangeState, date) {
      let start = false,
        inRange = false,
        end = false,
        disabled = false,
        selected = false;

      const currValue = this.calcCellValue({ ...item, year: this.year }, "item");

      const maxDisabled = this.endLimitDate && this.calcCellValue(this.endLimitDate, "disableMIn") || Infinity;
      const minDisabled = this.startLimitDate && this.calcCellValue(this.startLimitDate, "disableMax") || -Infinity;

      // 禁用 不包含
      disabled = currValue < minDisabled || currValue > maxDisabled;

      if (this.isRange) {
        if (!minDate) {
          return {
            ...item,
            start,
            inRange,
            end,
            disabled,
            selected
          };
        }

        const minValue =  this.calcCellValue(minDate, "minvalue");

        let maxValue;

        if (rangeState.selecting) {
          maxValue = this.calcCellValue(rangeState, "rangeState");

          if (!isNaN(maxValue) && maxValue !== minValue) {
            if (minValue <= maxValue) {
              start = selected = currValue === minValue;
              end = currValue === maxValue;
              inRange = (currValue >= minValue && currValue <= maxValue);
            } else {
              start = currValue === maxValue;
              end = selected = currValue === minValue;
              inRange = (currValue <= minValue && currValue >= maxValue);
            }

          } else {
            start = end = selected = inRange = currValue === minValue;
          }

        } else {
          if (maxDate) {
            maxValue = this.calcCellValue(maxDate, "maxValue");

            if (currValue === minValue) {
              start = selected = currValue === minValue;
            }

            if (currValue === maxValue) {
              end = selected = currValue === maxValue;
            }

            inRange = currValue >= minValue && currValue <= maxValue;
          }
        }
      } else {
        if (date) {
          const value =  this.calcCellValue(date, "value");

          selected = currValue === value;
        }
      }

      return {
        ...item,
        start,
        inRange,
        end,
        disabled,
        selected
      };
    },

    calcCellValue(target) {
      return target.year * 10000 + (target.page - 1) * this.pageCount + target.row * this.singleRowMaxCol + target.column;
    }
  }
};
</script>
